import { unwrapResult } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { processSmartDeviceCPR, transformCPRData } from "../../../../api/cprServices";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import useSmartDeviceFlow from "../../../../hooks/useSmartDeviceFlow";
import useSmartDeviceFormData from "../../../../hooks/useSmartDeviceFormData";
import {
  fetchNationalitiesList,
  selectNationalities,
  selectNationalitiesLoading,
} from "../../../../redux/features/constants/nationalitiesSlice";
import { selectLoginTokenInfo } from "../../../../redux/features/loginRegisterSlice";
import {
  fetchProfileInfo,
  selectProfileData,
} from "../../../../redux/features/profileSlice";
import {
  clearAllQuotes,
  fetchPartyCode,
  fetchSmartDeviceQuote,
  selectPartyCode,
  selectSmartDeviceQuote,
  selectSmartDeviceQuoteError,
  selectSmartDeviceQuoteLoading,
} from "../../../../redux/features/quoteSlice";
import { selectSmartDeviceForm } from "../../../../redux/features/smartDeviceFormSlice";
import {
  daysOptions,
  monthsOptions,
  yearsOptions,
} from "../../../../utils/calenderOptions";
import {
  formatPremiumValue,
  formatPremiumWithCurrency,
} from "../../../../utils/index";
import Input from "../../../common/Input/Input";
import FileUploadInput from "../../../common/newFileUploadInput/FileUploadInput";
import SelectInput from "../../../common/selectInput/selectInput";
import SingleDatePicker from "../../../common/SingleDatePicker/SingleDatePicker";
import FormTabs from "../../../common/tabs/formTabs";
import TelInput from "../../../common/telInput/telInput";
import TravelDetailsTable from "../../../common/TravelDetailsTable/TravelDetailsTable";
import BottomStickyBtnLayout from "../../layouts/BottomStickyBtnLayout";
import SmartDeviceJourneyLayout from "../../layouts/SmartDeviceJourneyLayout";
import SmartDeviceSummaryModal from "../../modals/smartDeviceSummaryModal";

// Gender tabs
const genderTabs = [
  { label_en: "Male", label_ar: "ذكر", type: "Male" },
  { label_en: "Female", label_ar: "أنثى", type: "Female" },
];

export const SMART_DEVICE_CONTACT_DETAILS_FORM =
  "SMART_DEVICE_CONTACT_DETAILS_FORM";

const ContactDetails = () => {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const { handleNextSubstep } = useSmartDeviceFlow();
  const dispatch = useDispatch();
  const profileData = useSelector(selectProfileData);
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const smartDeviceFormData = useSelector(selectSmartDeviceForm);
  const smartDeviceQuote = useSelector(selectSmartDeviceQuote);
  const quoteLoading = useSelector(selectSmartDeviceQuoteLoading);
  const quoteError = useSelector(selectSmartDeviceQuoteError);
  const partyCode = useSelector(selectPartyCode);

  // Get current locale for number formatting
  const currentLocale = i18n.resolvedLanguage === "ar" ? "en-US" : "en-US";

  // Helper function for footer currency formatting with English numerals
  const formatFooterCurrency = (amount) => {
    if (i18n.resolvedLanguage === "ar") {
      // Arabic: use English numerals and reverse order (amount currency)
      return `${formatPremiumValue(amount, "en-US", 3)} د.ب`;
    } else {
      // English: standard format (currency amount)
      return formatPremiumWithCurrency(amount, "BHD", "en-US");
    }
  };

  // Determine if user is guest
  const isGuest = !Boolean(loginTokenInfo);

  // Read guest basic info captured on the start page
  const sdBasicInfo = smartDeviceFormData?.SMART_DEVICE_BASIC_INFO;
  const hasGuestBasicInfo = isGuest && !!(
    sdBasicInfo?.fullName &&
    sdBasicInfo?.email &&
    sdBasicInfo?.contactNumber &&
    sdBasicInfo?.birthDate
  );

  // Fetch profile data if not already loaded and user is logged in
  useEffect(() => {
    if (!isGuest && !profileData) {
      dispatch(fetchProfileInfo());
    }
  }, [isGuest, profileData, dispatch]);

  // Fetch party code for logged-in users
  useEffect(() => {
    if (!isGuest && profileData?.CPR && !partyCode) {
      console.log("Fetching party code for CPR:", profileData.CPR);
      dispatch(fetchPartyCode(profileData.CPR));
    }
  }, [isGuest, profileData, partyCode, dispatch]);

  // Nationality data from Redux
  const nationalityOptions = useSelector(selectNationalities);
  const nationalitiesLoading = useSelector(selectNationalitiesLoading);

  // Fetch nationalities list on mount
  useEffect(() => {
    if (nationalityOptions.length === 0 && !nationalitiesLoading) {
      dispatch(fetchNationalitiesList());
    }
  }, [dispatch, nationalityOptions.length, nationalitiesLoading]);

  // Smart device form data hooks
  const { saveSmartDeviceDataInRedux, redux_form_data } =
    useSmartDeviceFormData(SMART_DEVICE_CONTACT_DETAILS_FORM, () => {});

  // Helper function to create default user data
  const createDefaultUserData = () => ({
    cprNumber: "",
    fullName: "",
    emailAddress: "",
    mobileNumber: "",
    nationality: "", // No default nationality - show placeholder
    gender: "", // No default gender selection
    dateOfBirth: {
      day: "",
      month: "",
      year: "",
      fullDate: "",
    },
    cprUploaded: false,
    cprFiles: [],
    // CPR processing state
    cprProcessing: false,
    cprData: null,
    cprProcessed: false,
  });

  const [userData, setUserData] = useState(createDefaultUserData());

  // State for logged-in user selection
  const [policyFor, setPolicyFor] = useState(
    isGuest ? "someone_else" : "myself"
  );

  // For guests in 'For Myself' mode: prefill name/email/mobile/DOB from start basic info
  useEffect(() => {
    if (!hasGuestBasicInfo) return;
    if (policyFor !== "myself") return;
    const birthDate = sdBasicInfo?.birthDate || "";
    let dobObj = { day: "", month: "", year: "", fullDate: "" };
    if (birthDate) {
      const parts = birthDate.split("-");
      if (parts.length === 3) {
        dobObj = { year: parts[0], month: parts[1], day: parts[2], fullDate: birthDate };
      }
    }
    setUserData((prev) => ({
      ...prev,
      fullName: prev.fullName || sdBasicInfo?.fullName || "",
      emailAddress: prev.emailAddress || sdBasicInfo?.email || "",
      mobileNumber: prev.mobileNumber || sdBasicInfo?.contactNumber || "",
      dateOfBirth: prev.dateOfBirth?.fullDate ? prev.dateOfBirth : dobObj,
    }));
  }, [hasGuestBasicInfo, policyFor, sdBasicInfo, userData.fullName, userData.emailAddress, userData.mobileNumber]);

  // State to track touched fields and form validation attempts
  const [touchedFields, setTouchedFields] = useState({});
  const [hasValidationAttempt, setHasValidationAttempt] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [hasCalledQuoteApi, setHasCalledQuoteApi] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [isDebouncing, setIsDebouncing] = useState(false);

  // Reset API call flag when device selection or purchase details change
  useEffect(() => {
    const deviceSelection = smartDeviceFormData?.SMART_DEVICE_SELECTION_FORM;
    const purchaseDetails =
      smartDeviceFormData?.SMART_DEVICE_PURCHASE_DETAILS_FORM;

    // If data changed and API was called before, reset the flag to allow retry
    if (hasCalledQuoteApi && (deviceSelection || purchaseDetails)) {
      console.log("Device/purchase data changed - resetting API call flag");
      setHasCalledQuoteApi(false);
    }
  }, [
    smartDeviceFormData?.SMART_DEVICE_SELECTION_FORM?.imeiNumber,
    smartDeviceFormData?.SMART_DEVICE_SELECTION_FORM?.brand,
    smartDeviceFormData?.SMART_DEVICE_SELECTION_FORM?.deviceType,
    smartDeviceFormData?.SMART_DEVICE_PURCHASE_DETAILS_FORM?.devicePrice,
    smartDeviceFormData?.SMART_DEVICE_PURCHASE_DETAILS_FORM?.purchaseDate,
  ]);

  // Initialize with existing quote from Redux if available
  // CRITICAL: Only mark as called if we have a valid quote for "someone_else" flow
  // For logged-in "For Myself", always let the main useEffect handle it
  useEffect(() => {
    // Only apply this logic for "someone_else" flow
    if (policyFor !== "someone_else") {
      return;
    }

    const hasUserData = userData?.cprNumber && userData?.fullName;
    const quoteCPRMatchesUserCPR =
      smartDeviceQuote?.cpr &&
      userData?.cprNumber &&
      smartDeviceQuote.cpr === userData.cprNumber;

    if (
      smartDeviceQuote?.totalPremium &&
      smartDeviceQuote.totalPremium > 0 &&
      !hasCalledQuoteApi &&
      hasUserData &&
      quoteCPRMatchesUserCPR
    ) {
      console.log("✅ Quote already exists from Redux for 'someone_else', marking as called");
      setHasCalledQuoteApi(true);
    }
  }, [smartDeviceQuote, hasCalledQuoteApi, userData, policyFor]);

  // Auto-call quote API with debounce for "someone_else"
  useEffect(() => {
    console.log("=== Quote API Auto-Call Check ===");
    console.log("policyFor:", policyFor);
    console.log("isGuest:", isGuest);
    console.log("hasCalledQuoteApi:", hasCalledQuoteApi);
    console.log("userData:", userData);

    // Check if we have device and purchase data
    const deviceSelection = smartDeviceFormData?.SMART_DEVICE_SELECTION_FORM;
    const purchaseDetails =
      smartDeviceFormData?.SMART_DEVICE_PURCHASE_DETAILS_FORM;

    if (!deviceSelection || !purchaseDetails) {
      console.log("❌ Missing device/purchase data - skipping API call");
      setIsDebouncing(false);
      return;
    }

    // If API was already called, don't call again
    if (hasCalledQuoteApi) {
      console.log("❌ API already called - skipping");
      setIsDebouncing(false);
      return;
    }

    // Check if all required fields are filled
    // For "For Myself" (logged-in user): dateOfBirth is optional
    // For "For Someone Else": dateOfBirth, CPR files, and valid 9-digit CPR are required
    const hasAllFields =
      policyFor === "myself" && !isGuest
        ? userData.fullName &&
          userData.cprNumber &&
          userData.emailAddress &&
          userData.mobileNumber &&
          userData.nationality &&
          userData.gender
        : // dateOfBirth is optional for logged-in users
          userData.fullName &&
          userData.cprNumber &&
          userData.cprNumber.length === 9 && // CPR must be exactly 9 digits
          userData.emailAddress &&
          userData.mobileNumber &&
          userData.nationality &&
          userData.gender &&
          userData.dateOfBirth?.fullDate &&
          userData.cprFiles?.length > 0; // CPR files required for "For Someone Else"

    if (!hasAllFields) {
      console.log("❌ Not all fields filled - skipping API call");
      console.log("Field check details:", {
        fullName: !!userData.fullName,
        cprNumber: !!userData.cprNumber,
        cprLength: userData.cprNumber?.length,
        emailAddress: !!userData.emailAddress,
        mobileNumber: !!userData.mobileNumber,
        nationality: !!userData.nationality,
        gender: !!userData.gender,
        dateOfBirth: !!userData.dateOfBirth?.fullDate,
        cprFiles: userData.cprFiles?.length || 0,
      });
      setIsDebouncing(false);
      return;
    }

    // SCENARIO 1 & 3: For Myself (logged-in user) - NO DEBOUNCE
    if (!isGuest && policyFor === "myself") {
      console.log("✅ Calling Quote API for 'For Myself' (logged-in user)");
      callQuoteAPI();
      return;
    }

    // SCENARIO 2: For Someone Else (all fields filled + CPR uploaded) - WITH DEBOUNCE
    if (policyFor === "someone_else") {
      // Extra check for "someone_else": Ensure nationality is a valid number code (not text)
      if (userData.nationality && (typeof userData.nationality !== 'number' && isNaN(Number(userData.nationality)))) {
        console.log("❌ Nationality is not a valid code - skipping API call:", userData.nationality);
        setIsDebouncing(false);
        return;
      }
      // Check if there are any validation errors
      const validationErrors = validateFields(userData);
      const hasValidationErrors = Object.keys(validationErrors).length > 0;

      if (hasValidationErrors) {
        console.log("❌ Form has validation errors - skipping API call");
        console.log("Validation errors:", validationErrors);
        setIsDebouncing(false);
        return;
      }

      console.log(
        "⏳ All fields filled and valid for 'For Someone Else' - starting 2.5s debounce"
      );
      setIsDebouncing(true);

      // Debounce for 2.5 seconds before calling API
      const debounceTimer = setTimeout(() => {
        console.log("✅ Debounce complete - calling Quote API");
        callQuoteAPI();
        setIsDebouncing(false);
      }, 2500); // 2.5 seconds debounce

      // Cleanup function to clear timeout if userData changes
      return () => {
        console.log("🧹 Clearing debounce timer");
        clearTimeout(debounceTimer);
        setIsDebouncing(false);
      };
    }

    console.log("❌ Conditions not met - skipping API call");
    setIsDebouncing(false);
  }, [userData, smartDeviceFormData, isGuest, policyFor, hasCalledQuoteApi, partyCode, profileData]); // Added partyCode and profileData to re-run when fetched

  // Helper function to format profile date - UPDATED FOR CONSISTENT FORMAT
  const formatProfileDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Helper function to parse profile date for passenger form - COPIED FROM TRAVEL
  const parseProfileDateForForm = (dateString) => {
    if (!dateString) return { day: "", month: "", year: "", fullDate: "" };
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn("Invalid date string:", dateString);
        return { day: "", month: "", year: "", fullDate: "" };
      }

      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear().toString();
      const fullDate = `${year}-${month}-${day}`;

      return {
        day,
        month,
        year,
        fullDate,
      };
    } catch (error) {
      console.error("Error parsing profile date:", error);
      return { day: "", month: "", year: "", fullDate: "" };
    }
  };


  // Real logged-in user data from profile
  const loggedInUserData = {
    cprNumber: profileData?.CPR || "",
    fullName: profileData
      ? `${profileData.FirstName || ""} ${profileData.LastName || ""}`.trim()
      : "",
    nationality: profileData?.Nationality || "Bahraini",
    gender: profileData?.Gender === "M" ? "Male" : profileData?.Gender === "F" ? "Female" : "Male", // Convert M/F to Male/Female for display
    dateOfBirth: formatProfileDate(profileData?.DateOfBirth || profileData?.DOB) || "",
    email: profileData?.Email || "",
    mobile: profileData?.PhoneNumber || "",
  };

  // Helper function to get nationality code from string name
  const getNationalityCode = (nationalityString) => {
    if (!nationalityString) return ""; // Return empty for placeholder

    // If it's already a number, return it
    if (typeof nationalityString === "number") return nationalityString;

    // Try to find the nationality in the options
    const found = nationalityOptions?.find(
      (opt) => opt.value?.toUpperCase() === nationalityString?.toUpperCase()
    );

    return found ? found.code : ""; // Return empty if not found
  };

  // Helper function to create user data from logged-in profile
  const createLoggedInUserData = () => {
    const data = {
      cprNumber: loggedInUserData.cprNumber,
      fullName: loggedInUserData.fullName,
      emailAddress: loggedInUserData.email,
      mobileNumber: loggedInUserData.mobile,
      nationality: getNationalityCode(loggedInUserData.nationality),
      gender: loggedInUserData.gender,
      dateOfBirth: parseProfileDateForForm(profileData?.DateOfBirth || profileData?.DOB),
      cprUploaded: true, // Assume logged-in user has verified CPR
      cprFiles: [], // Empty files for logged-in user
    };

    return data;
  };

  // Build data for displaying logged-in user info
  const buildLoggedInUserDisplayData = () => {
    const data = {
      [translate("CPR Number", "رقم الهوية الوطنية")]:
        loggedInUserData.cprNumber,
      [translate("Full Name", "الاسم الكامل")]: loggedInUserData.fullName,
      [translate("Nationality", "الجنسية")]: translate(
        loggedInUserData.nationality,
        "بحريني"
      ),
      [translate("Gender", "الجنس")]: translate(loggedInUserData.gender, "ذكر"),
    };

    // Always show Date of Birth field, empty if no data
    data[translate("Date of Birth", "تاريخ الميلاد")] =
      loggedInUserData.dateOfBirth || "";

    return data;
  };

  // Handle policy selection change
  const handlePolicyForChange = (selection) => {
    // CRITICAL: Prevent tab switching while quote API is loading or debouncing
    if (quoteLoading || isDebouncing) {
      console.log("❌ Cannot switch tabs - quote API is loading/debouncing");
      toast.warning(
        translate(
          "Please wait for the quote to finish loading before switching.",
          "يرجى الانتظار حتى ينتهي تحميل التسعيرة قبل التبديل."
        )
      );
      return;
    }

    console.log("=== Tab Switch ===");
    console.log("Switching to:", selection);
    console.log("Previous policyFor:", policyFor);

    setPolicyFor(selection);

    // IMPORTANT: Reset API call flag to allow new quote with different user data
    console.log("🔄 Resetting hasCalledQuoteApi to false");
    setHasCalledQuoteApi(false);

    // IMPORTANT: Clear quote data when switching tabs
    console.log(
      "🔄 Clearing quote data (total premium and show summary will be reset)"
    );
    dispatch(clearAllQuotes());

    if (selection === "myself" && !isGuest) {
      // SCENARIO 3: Switching back to "For Myself"
      console.log("✅ Loading logged-in user data");
      const loggedInUser = createLoggedInUserData();
      setUserData(loggedInUser);
      console.log("✅ userData set - API will be called by useEffect");
    } else {
      // SCENARIO 2: Switching to "For Someone Else"
      console.log("✅ Resetting to empty form for 'For Someone Else'");
      setUserData(createDefaultUserData());
      console.log("✅ User will fill data - API will be called when complete");
    }

    // Clear field errors when changing selection
    setFieldErrors({});
  };

  // Initialize user data based on saved data
  useEffect(() => {
    console.log("=== Initialization useEffect ===");
    console.log("redux_form_data:", redux_form_data);
    console.log("isGuest:", isGuest);

    // Restore saved contact details if available
    // FIXED: Check for passengers array (saved structure) instead of userData
    if (redux_form_data && redux_form_data.passengers && redux_form_data.passengers.length > 0) {
      console.log("✅ Restoring saved form data from passengers array");
      const savedUserData = redux_form_data.passengers[0]; // Get first passenger
      setUserData(savedUserData);
      setPolicyFor(
        redux_form_data.policyFor || (isGuest ? "someone_else" : "myself")
      );
      console.log("✅ Restored userData:", savedUserData);
      console.log("✅ Restored policyFor:", redux_form_data.policyFor);
      return; // Exit early to prevent further initialization
    } else {
      // Initialize with defaults
      const initialPolicyFor = isGuest ? "someone_else" : "myself";
      console.log("✅ Initializing with policyFor:", initialPolicyFor);
      setPolicyFor(initialPolicyFor);

      if (!isGuest) {
        console.log("✅ Setting logged-in user data on init");
        const loggedInUser = createLoggedInUserData();
        setUserData(loggedInUser);
        console.log("✅ Initial userData set:", loggedInUser);
      } else {
        console.log("✅ Setting empty user data for guest");
        const defaultUser = createDefaultUserData();
        setUserData(defaultUser);
      }
    }
  }, [redux_form_data, isGuest]); // Depend on redux_form_data and isGuest

  // Update user data when profile data is loaded OR when switching back to "For Myself"
  useEffect(() => {
    console.log("=== Profile Data Update Check ===");
    console.log("isGuest:", isGuest);
    console.log("policyFor:", policyFor);
    console.log("profileData:", profileData);
    console.log("redux_form_data:", redux_form_data);
    console.log("hasCalledQuoteApi:", hasCalledQuoteApi);

    // Update userData whenever:
    // 1. User is logged in AND
    // 2. "For Myself" is selected AND
    // 3. Profile data is available AND
    // 4. There's NO saved data for "someone_else" (don't overwrite restored data)
    const hasSavedSomeoneElseData = redux_form_data?.policyFor === "someone_else";

    if (!isGuest && policyFor === "myself" && profileData && !hasSavedSomeoneElseData) {
      console.log("✅ Updating userData for 'For Myself' with profile data");
      const loggedInUser = createLoggedInUserData();
      setUserData(loggedInUser);
      console.log("✅ userData updated with:", loggedInUser);

      // CRITICAL: Reset hasCalledQuoteApi to allow API call with logged-in user data
      // Only reset if it was previously called (to avoid infinite loop)
      if (hasCalledQuoteApi) {
        console.log("🔄 Resetting hasCalledQuoteApi to allow quote API call with logged-in user data");
        setHasCalledQuoteApi(false);
      }
    } else if (hasSavedSomeoneElseData && policyFor === "myself") {
      console.log("⏭️ Skipping profile data update - saved 'someone_else' data exists");
    }
  }, [profileData, isGuest, policyFor, redux_form_data]); // DO NOT add hasCalledQuoteApi to avoid infinite loop

  // Auto-call quote API for logged-in "For Myself" users on mount
  useEffect(() => {
    console.log("=== Auto Quote API Trigger (On Mount) ===");
    console.log("isGuest:", isGuest);
    console.log("policyFor:", policyFor);
    console.log("profileData exists:", !!profileData);
    console.log("hasCalledQuoteApi:", hasCalledQuoteApi);

    // Only for logged-in users selecting "For Myself"
    if (isGuest || policyFor !== "myself" || !profileData) {
      console.log("❌ Skipping auto-call - conditions not met");
      return;
    }

    // If API already called, skip
    if (hasCalledQuoteApi) {
      console.log("❌ API already called");
      return;
    }

    // Check if we have device/purchase data
    const deviceSelection = smartDeviceFormData?.SMART_DEVICE_SELECTION_FORM;
    const purchaseDetails = smartDeviceFormData?.SMART_DEVICE_PURCHASE_DETAILS_FORM;

    if (!deviceSelection || !purchaseDetails) {
      console.log("❌ Missing device/purchase data");
      return;
    }

    // Wait for party code
    if (!partyCode) {
      console.log("⏳ Waiting for party code...");
      return;
    }

    console.log("✅ All conditions met - auto-calling quote API for logged-in user!");

    // Small delay to ensure userData is populated
    setTimeout(() => {
      callQuoteAPI();
    }, 100);
  }, [profileData, isGuest, policyFor, hasCalledQuoteApi, partyCode, smartDeviceFormData]);

  // Helper function to get field error
  const getFieldError = (field) => {
    return fieldErrors[field] || "";
  };

  // Custom validation function with proper priority
  const validateFields = (user) => {
    const newErrors = {};

    // CPR validation - required first, then format
    if (!user.cprNumber?.trim()) {
      newErrors.cprNumber = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (user.cprNumber.length !== 9) {
      newErrors.cprNumber = translate(
        "CPR number must be 9 digits.",
        "يجب أن يتكون رقم الهوية الوطنية من 9 أرقام."
      );
    }

    // Full name validation - required first, then format (allow both English and Arabic characters)
    if (!user.fullName?.trim()) {
      newErrors.fullName = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (!/^[a-zA-Z\u0600-\u06FF\s\.]+$/.test(user.fullName)) {
      newErrors.fullName = translate(
        "Name can only contain alphabetic characters, spaces, and periods.",
        "يجب أن يحتوي الاسم على حروف أبجدية ومسافات ونقاط فقط."
      );
    }

    // Email validation - required first, then format
    if (!user.emailAddress?.trim()) {
      newErrors.emailAddress = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(user.emailAddress)) {
      newErrors.emailAddress = translate(
        "Invalid email address.",
        "البريد الإلكتروني غير صحيح."
      );
    }

    // Mobile validation - required first, then length and numeric check
    if (!user.mobileNumber?.trim()) {
      newErrors.mobileNumber = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (
      !/^\d+$/.test(user.mobileNumber) ||
      user.mobileNumber.length !== 8
    ) {
      newErrors.mobileNumber = translate(
        "Invalid mobile number.",
        "رقم الهاتف المحمول غير صحيح."
      );
    }

    // Nationality validation - required only
    if (!user.nationality) {
      newErrors.nationality = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    // Gender validation - required only
    if (!user.gender?.trim()) {
      newErrors.gender = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    // Date of birth validation - check if any part of date is provided
    const hasAnyDatePart =
      user.dateOfBirth.day || user.dateOfBirth.month || user.dateOfBirth.year;

    if (!hasAnyDatePart) {
      newErrors["dateOfBirth.day"] = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (user.dateOfBirth.fullDate?.trim()) {
      // Date of birth future validation
      const birthDate = new Date(user.dateOfBirth.fullDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (birthDate > today) {
        newErrors["dateOfBirth.day"] = translate(
          "Date of birth cannot be in the future.",
          "لا يمكن أن يكون تاريخ الميلاد في المستقبل."
        );
      } else {
        // Age validation (must be 18+)
        const age = Math.floor(
          (today - birthDate) / (365.25 * 24 * 60 * 60 * 1000)
        );
        if (age < 18) {
          newErrors["dateOfBirth.day"] = translate(
            "You must be 18 years or older.",
            "يجب أن يكون عمرك 18 عامًا أو أكثر."
          );
        }
      }
    }

    return newErrors;
  };

  const handleInputChange = (field, value) => {
    let processedValue = value;

    // Apply input formatting based on field type
    if (field === "fullName") {
      // Full Name: Allow alphabetic characters, spaces, and periods
      processedValue = value;
    }

    setUserData((prevData) => {
      const updatedData = { ...prevData };
      if (field.includes(".")) {
        const [parentField, childField] = field.split(".");
        updatedData[parentField] = {
          ...updatedData[parentField],
          [childField]: processedValue,
        };
      } else {
        updatedData[field] = processedValue;
      }

      return updatedData;
    });

    // Validate field after state update
    setTimeout(() => {
      const updatedData = { ...userData };
      if (field.includes(".")) {
        const [parentField, childField] = field.split(".");
        updatedData[parentField] = {
          ...updatedData[parentField],
          [childField]: processedValue,
        };
      } else {
        updatedData[field] = processedValue;
      }

      const userErrors = validateFields(updatedData);

      // Update field errors
      setFieldErrors((prevErrors) => ({
        ...prevErrors,
        [field]: userErrors[field] || "",
      }));
    }, 0);
  };

  // Mobile date picker handler
  const handleMobileDatePickerSelect = (dateString) => {
    if (!dateString) return;

    // Parse the date string (YYYY-MM-DD format from mobile date picker)
    const [year, month, day] = dateString.split("-");

    const newDOB = {
      day,
      month,
      year,
      fullDate: dateString,
    };

    setUserData((prevData) => ({
      ...prevData,
      dateOfBirth: newDOB,
    }));

    // Validate date of birth in real-time
    const updatedUserData = {
      ...userData,
      dateOfBirth: newDOB,
    };
    const validationErrors = validateFields(updatedUserData);
    setFieldErrors((prevErrors) => ({
      ...prevErrors,
      "dateOfBirth.day": validationErrors["dateOfBirth.day"] || "",
    }));
  };

  const handleFileSelect = async (fileData) => {
    // Mark CPR field as touched
    setTouchedFields((prev) => ({
      ...prev,
      cpr: true,
    }));

    // Update user with uploaded file
    setUserData((prevData) => {
      const currentFiles = prevData.cprFiles || [];

      return {
        ...prevData,
        cprFiles: [...currentFiles, fileData],
        cprUploaded: true,
        cprProcessing: true, // Start processing state
      };
    });

    // Clear any field errors when files are uploaded
    setFieldErrors((prevErrors) => ({
      ...prevErrors,
      cpr: "",
    }));

    // Process CPR with Vista OCR in background
    if (fileData && fileData.file) {
      try {
        // Determine if this is front or back based on current file count
        const currentFileCount = userData.cprFiles?.length || 0;
        const side = currentFileCount === 0 ? 'front' : 'back';
        console.log(`Starting CPR processing (${side})`);

        const result = await processSmartDeviceCPR(fileData.file, side);

        if (result.success && result.data?.output?.status) {
          // CPR processing successful
          console.log("CPR processing completed successfully", result.data);

          const cprData = transformCPRData(result.data);

          // Auto-fill user data with CPR information
          setUserData((prevData) => {
            // CRITICAL: Convert nationality string to numeric code (same logic as Domestic Helper)
            let nationalityCode = prevData.nationality; // Keep previous value by default

            if (cprData.nationality) {
              const nationalityString = cprData.nationality.toString().trim();

              // Check if it's Bahraini nationality (handles "BAHRAINI", "BHR", "14", etc.)
              const isBahraini =
                nationalityString === "14" ||
                nationalityString.toLowerCase().includes("bahrain") ||
                nationalityString.toLowerCase().includes("بحرين") ||
                nationalityString.toUpperCase() === "BHR";

              // Try to find the nationality in options (works for both Bahraini and others)
              const foundNationality = nationalityOptions?.find(
                (opt) => {
                  const optValue = opt.value?.toString().toLowerCase();
                  const optLabel = opt.label?.toString().toLowerCase();
                  const optCode = opt.code?.toString();
                  const optId = opt.id?.toString();
                  const searchString = nationalityString.toLowerCase();

                  return (
                    optValue === searchString ||
                    optLabel === searchString ||
                    optCode === nationalityString ||
                    optId === nationalityString ||
                    (optLabel?.includes("bahrain") && isBahraini) ||
                    (optValue?.includes("bahrain") && isBahraini) ||
                    (optCode === "14" && isBahraini) ||
                    (optLabel?.includes("bhr") && nationalityString.toUpperCase() === "BHR") ||
                    (optValue?.includes("bhr") && nationalityString.toUpperCase() === "BHR")
                  );
                }
              );

              if (foundNationality) {
                // Use the actual value from the options (what SelectInput expects)
                nationalityCode = foundNationality.code || foundNationality.id || foundNationality.value;

                if (isBahraini) {
                  console.log(`⚠️ Bahraini nationality detected from CPR OCR - setting it but marking as invalid (code: ${nationalityCode})`);

                  // Set error immediately for Bahraini
                  setFieldErrors((prevErrors) => ({
                    ...prevErrors,
                    nationality: translate(
                      "Nationality cannot be Bahraini for Smart Device insurance.",
                      "لا يمكن أن تكون الجنسية بحرينية لتأمين الأجهزة الذكية."
                    ),
                  }));
                } else {
                  console.log(`✅ Converted nationality "${nationalityString}" to code: ${nationalityCode}`);
                }
              } else {
                // Couldn't find matching nationality - keep OCR value and let validation handle it
                nationalityCode = nationalityString;
                console.log(`⚠️ Couldn't find nationality code for: ${nationalityString}, using OCR value`);
              }
            }

            // CRITICAL: Only auto-fill fields that have valid non-empty values from OCR
            const updatedUserData = {
              ...prevData,
              // Only set if OCR provided a valid value (not empty/null/undefined)
              cprNumber: cprData.cprNumber?.toString().trim() || prevData.cprNumber,
              fullName: cprData.fullName?.trim() || prevData.fullName,
              dateOfBirth: cprData.dateOfBirth?.day && cprData.dateOfBirth?.month && cprData.dateOfBirth?.year
                ? {
                    ...cprData.dateOfBirth,
                    fullDate:
                      `${cprData.dateOfBirth.year}-${cprData.dateOfBirth.month.padStart(2, "0")}-${cprData.dateOfBirth.day.padStart(2, "0")}`,
                  }
                : prevData.dateOfBirth,
              gender: cprData.gender?.trim() || prevData.gender,
              nationality: nationalityCode, // Use converted numeric code
              // Update processing state
              cprProcessing: false,
              cprData: cprData,
              cprProcessed: true,
            };

            // CRITICAL: Validate all auto-filled fields IMMEDIATELY (not in setTimeout)
            const validationErrors = validateFields(updatedUserData);
            setFieldErrors(validationErrors);

            console.log("✅ CPR OCR data validated immediately after auto-fill");
            console.log("Validation errors:", validationErrors);

            return updatedUserData;
          });

          toast.success(
            translate(
              "CPR processed successfully!",
              "تم معالجة بطاقة الهوية بنجاح!"
            )
          );
        } else {
          // CPR processing failed
          console.warn("CPR processing failed", result.error || result.data);

          setUserData((prevData) => ({
            ...prevData,
            cprProcessing: false,
          }));

          // Show error toast
          toast.error(
            translate(
              "Failed to process CPR. Please fill the form manually.",
              "فشل في معالجة الهوية. يرجى ملء النموذج يدوياً."
            )
          );
        }
      } catch (error) {
        console.error("CPR processing error", error);

        setUserData((prevData) => ({
          ...prevData,
          cprProcessing: false,
        }));

        // Show error toast
        toast.error(
          translate(
            "Failed to process CPR. Please fill the form manually.",
            "فشل في معالجة الهوية. يرجى ملء النموذج يدوياً."
          )
        );
      }
    }
  };

  const handleFileRemove = () => {
    // Mark CPR field as touched
    setTouchedFields((prev) => ({
      ...prev,
      cpr: true,
    }));

    // Clear CPR data and processing state
    setUserData((prevData) => ({
      ...prevData,
      cprUploaded: false,
      cprFiles: [],
      cprProcessing: false,
      cprData: null,
      cprProcessed: false,
      // Don't clear form data - let user decide if they want to keep it
    }));
  };

  // Function to check if CPR files are uploaded
  const getCprUploadError = () => {
    // Skip CPR validation for logged-in user when "For Myself" is selected
    if (!isGuest && policyFor === "myself") {
      return "";
    }

    // Only show error if field has been touched OR validation has been attempted
    const fieldTouched = touchedFields.cpr;
    if (!fieldTouched && !hasValidationAttempt) {
      return "";
    }

    if (!userData.cprFiles || userData.cprFiles.length === 0) {
      return translate("This field is required.", "هذا الحقل مطلوب.");
    }
    return "";
  };

  const handleContinue = async () => {
    console.log("=== handleContinue clicked ===");
    console.log("isGuest:", isGuest);
    console.log("policyFor:", policyFor);
    console.log("userData:", userData);

    // Mark that validation has been attempted
    setHasValidationAttempt(true);

    let allValid = true;
    const newFieldErrors = {};

    // Skip validation for logged-in user when "For Myself" is selected
    if (!isGuest && policyFor === "myself") {
      console.log("=== Logged-in user - proceeding ===");
      // Logged-in user data is already validated
      const contactDetailsData = {
        passengers: [userData],
        policyFor,
      };

      await saveSmartDeviceDataInRedux(contactDetailsData);

      // If quote was already called, just proceed
      if (hasCalledQuoteApi && smartDeviceQuote) {
        console.log("Using existing quote, proceeding to next step");
        handleNextSubstep();
        return;
      }

      // CRITICAL: For logged-in users, wait for partyCode to be fetched
      if (!isGuest) {
        if (!partyCode) {
          console.log("⏳ Waiting for party code to be fetched for logged-in user...");
          // Trigger fetch if not already fetching
          if (profileData?.CPR) {
            dispatch(fetchPartyCode(profileData.CPR));
          }
          toast.error(
            translate(
              "Loading account information. Please try again in a moment.",
              "جاري تحميل معلومات الحساب. يرجى المحاولة مرة أخرى بعد لحظة."
            )
          );
          return;
        }
      }

      console.log("=== Smart Device Quote Party Code Selection ===");
      console.log("Redux partyCode:", partyCode);
      console.log("User CPR:", profileData?.CPR);
      console.log("Is logged in:", !isGuest);
      console.log("Effective party code:", partyCode || "FALCON");

      // Build complete form data for quote API call
      const completeFormData = {
        ...smartDeviceFormData,
        SMART_DEVICE_CONTACT_DETAILS_FORM: contactDetailsData,
      };

      // Call the quote API if not already called
      try {
        console.log("=== Calling Smart Device Quote API (on Continue) ===");
        console.log("Complete form data:", completeFormData);

        const quoteAction = await dispatch(
          fetchSmartDeviceQuote(completeFormData)
        );
        const quoteResult = unwrapResult(quoteAction);

        console.log("Quote API result:", quoteResult);

        if (quoteResult.result?.success) {
          console.log(
            "✅ Smart Device Quote successful:",
            quoteResult.result.data
          );
          setHasCalledQuoteApi(true);
          toast.success(
            translate(
              "Quote generated successfully!",
              "تم إنشاء التسعيرة بنجاح!"
            )
          );
          // Proceed to next step
          handleNextSubstep();
        } else {
          console.error("❌ Quote API failed:", quoteResult.result?.error);
          toast.error(
            translate(
              quoteResult.result?.error ||
                "Failed to get quote. Please try again.",
              "فشل في الحصول على التسعيرة. يرجى المحاولة مرة أخرى."
            )
          );
        }
      } catch (error) {
        console.error("Quote API exception:", error);
        toast.error(
          translate(
            "Error getting quote. Please try again.",
            "خطأ في الحصول على التسعيرة. يرجى المحاولة مرة أخرى."
          )
        );
      }
      return;
    }

    // Validate user fields and collect errors
    const userErrors = validateFields(userData);

    // Add field errors
    Object.keys(userErrors).forEach((field) => {
      newFieldErrors[field] = userErrors[field];
      allValid = false;
    });

    // Check CPR upload requirement
    if (!userData.cprFiles || userData.cprFiles.length === 0) {
      allValid = false;
    }

    // Update field errors state to show validation messages
    setFieldErrors(newFieldErrors);

    if (allValid) {
      console.log("=== Validation passed - calling API ===");
      // Save contact details data to Redux first
      const contactDetailsData = {
        passengers: [userData],
        policyFor,
        // For someone_else the other person may not be in our system;
        // explicitly send FALCON so the thunk never uses the logged-in user's party code.
        ...(policyFor === "someone_else" && { partyCode: "FALCON" }),
      };

      await saveSmartDeviceDataInRedux(contactDetailsData);

      // If quote was already called, just proceed
      if (hasCalledQuoteApi && smartDeviceQuote) {
        console.log("Using existing quote, proceeding to next step");
        handleNextSubstep();
        return;
      }

      // CRITICAL: For logged-in users buying for themselves, wait for partyCode.
      // Skip when buying for someone else -- FALCON is used instead.
      if (!isGuest && policyFor !== "someone_else") {
        if (!partyCode) {
          console.log("⏳ Waiting for party code to be fetched for logged-in user...");
          // Trigger fetch if not already fetching
          if (profileData?.CPR) {
            dispatch(fetchPartyCode(profileData.CPR));
          }
          toast.error(
            translate(
              "Loading account information. Please try again in a moment.",
              "جاري تحميل معلومات الحساب. يرجى المحاولة مرة أخرى بعد لحظة."
            )
          );
          return;
        }
      }

      console.log("=== Smart Device Quote Party Code Selection ===");
      console.log("Redux partyCode:", partyCode);
      console.log("User CPR:", profileData?.CPR);
      console.log("Is logged in:", !isGuest);
      console.log("Effective party code:", partyCode || "FALCON");

      // Build complete form data for quote API call
      const completeFormData = {
        ...smartDeviceFormData,
        SMART_DEVICE_CONTACT_DETAILS_FORM: contactDetailsData,
      };

      // Call the quote API if not already called
      try {
        console.log("=== Calling Smart Device Quote API (on Continue) ===");
        console.log("Complete form data:", completeFormData);

        const quoteAction = await dispatch(
          fetchSmartDeviceQuote(completeFormData)
        );
        const quoteResult = unwrapResult(quoteAction);

        console.log("Quote API result:", quoteResult);

        if (quoteResult.result?.success) {
          console.log(
            "✅ Smart Device Quote successful:",
            quoteResult.result.data
          );
          setHasCalledQuoteApi(true);
          toast.success(
            translate(
              "Quote generated successfully!",
              "تم إنشاء التسعيرة بنجاح!"
            )
          );
          // Proceed to next step
          handleNextSubstep();
        } else {
          console.error("❌ Quote API failed:", quoteResult.result?.error);
          toast.error(
            translate(
              quoteResult.result?.error ||
                "Failed to get quote. Please try again.",
              "فشل في الحصول على التسعيرة. يرجى المحاولة مرة أخرى."
            )
          );
        }
      } catch (error) {
        console.error("Quote API exception:", error);
        toast.error(
          translate(
            "Error getting quote. Please try again.",
            "خطأ في الحصول على التسعيرة. يرجى المحاولة مرة أخرى."
          )
        );
      }
    } else {
      console.log("=== Validation failed ===");
      console.log("Field Errors:", newFieldErrors);
    }
  };

  // Function to call quote API using Redux thunk (following Personal Cyber pattern)
  const callQuoteAPI = async () => {
    if (quoteLoading || hasCalledQuoteApi) return;

    const contactDetailsData = {
      passengers: [userData],
      policyFor,
      // For someone_else the other person is not in our system; send FALCON so the
      // thunk does not accidentally use the logged-in user's party code.
      ...(policyFor === "someone_else" && { partyCode: "FALCON" }),
    };

    // CRITICAL: For logged-in users buying for themselves, wait for partyCode.
    // Skip when buying for someone else -- FALCON is used instead.
    if (!isGuest && policyFor !== "someone_else") {
      if (!partyCode) {
        console.log("⏳ Waiting for party code before auto-calling quote API...");
        // Trigger fetch if not already fetching
        if (profileData?.CPR) {
          dispatch(fetchPartyCode(profileData.CPR));
        }
        // Don't show error toast for auto-call, just skip it
        return;
      }
    }

    console.log("=== Smart Device Quote Party Code (Auto-call) ===");
    console.log("Redux partyCode:", partyCode);
    console.log("Effective party code:", partyCode || "FALCON");

    const completeFormData = {
      ...smartDeviceFormData,
      SMART_DEVICE_CONTACT_DETAILS_FORM: contactDetailsData,
    };

    try {
      console.log("=== Auto-calling Smart Device Quote API ===");
      console.log("Complete form data:", completeFormData);

      const quoteAction = await dispatch(
        fetchSmartDeviceQuote(completeFormData)
      );
      const quoteResult = unwrapResult(quoteAction);

      if (quoteResult.result?.success) {
        console.log("✅ Quote API successful (auto-called)");
        setHasCalledQuoteApi(true);
      } else {
        console.log("Quote API returned error:", quoteResult.result?.error);
        toast.error(
          translate(
            quoteResult.result?.error ||
              "Failed to get quote. Please check your device information.",
            "فشل في الحصول على التسعيرة. يرجى التحقق من معلومات الجهاز."
          )
        );
      }
    } catch (error) {
      console.error("Quote API exception (auto-call):", error);
      toast.error(
        translate(
          "Error getting quote. Please try again.",
          "خطأ في الحصول على التسعيرة. يرجى المحاولة مرة أخرى."
        )
      );
    }
  };

  const renderLoggedInSection = () => {
    if (isGuest) return null;

    // Show loading state if profile data is not available yet
    if (!profileData) {
      return (
        <div className="mb-6">
          <div className="bg-[#E3F8FF] rounded-lg p-4 mb-4">
            <div className="animate-pulse [animation-duration:3s]">
              <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-300 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      );
    }

    const userName = loggedInUserData.fullName || translate("User", "المستخدم");

    return (
      <div className="mb-4 mt-8 max767:px-5">
        <p className="text-[#48535B] text-base font-normal mb-2">
          {translate(
            <>
              You're logged in as <span className="font-bold">{userName}</span>. Choose whether to
              issue this policy under your name or someone else's.
            </>,
            <>
              أنت مسجل الدخول كـ <span className="font-bold">{userName}</span>. اختر ما إذا كنت
              تريد إصدار هذه الوثيقة باسمك أو باسم شخص آخر.
            </>
          )}
        </p>

        {/* Policy For Selection Tabs */}
        <div
          className={`${
            quoteLoading || isDebouncing
              ? "opacity-50 pointer-events-none cursor-not-allowed"
              : ""
          }`}
          title={
            quoteLoading || isDebouncing
              ? translate(
                  "Please wait for the quote to finish loading",
                  "يرجى الانتظار حتى ينتهي تحميل التسعيرة"
                )
              : ""
          }
        >
          <FormTabs
            label=""
            tabs={[
              {
                type: "myself",
                label_en: "For Myself",
                label_ar: "لنفسي",
              },
              {
                type: "someone_else",
                label_en: "For Someone Else",
                label_ar: "لشخص آخر",
              },
            ]}
            activeTab={policyFor}
            onTabClick={handlePolicyForChange}
          />
        </div>

        {/* Show logged-in user info table when "For Myself" is selected */}
        {policyFor === "myself" && (
          <div className="mt-6">
            {/* User Info Table - Clean design without headers */}
            <div className="mb-6">
              {profileData ? (
                <TravelDetailsTable
                  travelData={buildLoggedInUserDisplayData()}
                  className="!bg-[#F6F8F9] max767:bg-white !border-none max767:!border max767:border-[#E5E7EB] rounded-[16px] p-4 max767:p-0 overflow-hidden"
                  innerClassName="border border-[#E5E7EB] rounded-[10px]"
                />
              ) : (
                <div className="animate-pulse [animation-duration:3s] space-y-3">
                  <div className="h-4 bg-gray-300 rounded w-full"></div>
                  <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-300 rounded w-5/6"></div>
                  <div className="h-4 bg-gray-300 rounded w-2/3"></div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderUserForm = () => {
    return (
      <div className="mb-4 max767:px-5">
        {/* CPR Upload */}
        <div className="mb-3">
          <label className="mb-1 text-[#48535B] block text-base">
            {translate(
              "Upload CPR (front & back)",
              "تحميل الهوية الوطنية (من الأمام والخلف)"
            )}
          </label>
          <FileUploadInput
            name="cpr_user"
            onFileSelect={(fileData) => handleFileSelect(fileData)}
            onFileRemove={() => handleFileRemove()}
            onLoseFocus={() =>
              setTouchedFields((prev) => ({ ...prev, cpr: true }))
            }
            validTypes={["image/jpeg", "image/png", "application/pdf"]}
            maxSizeKB={5000}
            uploadToServer={true}
            multiple={true}
            maxFiles={2}
            disabled={userData.cprProcessing}
            initialFiles={userData.cprFiles || []}
          />

          {/* CPR Processing Status */}
          {userData.cprProcessing && (
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <p className="text-sm text-blue-700">
                  {translate(
                    "Processing CPR data...",
                    "جاري معالجة بيانات الهوية..."
                  )}
                </p>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                {translate(
                  "Please wait while we extract your information...",
                  "يرجى الانتظار بينما نستخرج معلوماتك..."
                )}
              </p>
            </div>
          )}

          {/* Show inline error for CPR upload */}
          {getCprUploadError() && (
            <div className="mt-2 text-red-600 text-sm">
              {getCprUploadError()}
            </div>
          )}
        </div>

        {/* CPR Number */}
        <div className="mb-3">
          <Input
            id="cprNumber"
            label={translate("CPR Number", "رقم الهوية الوطنية")}
            type="text"
            value={userData.cprNumber}
            handleChange={(e) => {
              const value = e.target.value.replace(/\D/g, ""); // Only allow digits
              handleInputChange("cprNumber", value);
            }}
            error={getFieldError("cprNumber")}
            disabled={userData.cprProcessing}
            maxLength={9}
          />
        </div>

        {!(hasGuestBasicInfo && policyFor === "myself") && (
          <>
        {/* Full Name */}
        <div className="mb-3">
          <Input
            id="fullName"
            label={translate("Full Name", "الاسم الكامل")}
            type="text"
            value={userData.fullName}
            handleChange={(e) => handleInputChange("fullName", e.target.value)}
            error={getFieldError("fullName")}
            disabled={userData.cprProcessing}
          />
        </div>

        {/* Email Address */}
        <div className="mb-3">
          <Input
            id="emailAddress"
            label={translate("Email Address", "عنوان البريد الإلكتروني")}
            type="email"
            value={userData.emailAddress}
            handleChange={(e) =>
              handleInputChange("emailAddress", e.target.value)
            }
            error={getFieldError("emailAddress")}
          />
        </div>

        {/* Mobile Number */}
        <div className="mb-3">
          <TelInput
            name="mobileNumber"
            value={userData.mobileNumber}
            error={getFieldError("mobileNumber")}
            onChange={(e) => handleInputChange(e.target.name, e.target.value)}
            label={translate("Mobile Number", "رقم الهاتف المحمول")}
          />
        </div>
          </>
        )}

        {/* Nationality */}
        <div className="mb-3">
          <SelectInput
            name="nationality"
            formData={userData}
            label={translate("Nationality", "الجنسية")}
            options_data={nationalityOptions || []}
            error={getFieldError("nationality")}
            handleChange={(e) =>
              !userData.cprProcessing &&
              handleInputChange(e.target.name, e.target.value)
            }
            default_placeholder={translate(
              "Please select an option.",
              "يرجى اختيار خيار."
            )}
            disabled={userData.cprProcessing || nationalitiesLoading}
          />
        </div>

        {/* Gender */}
        <div
          className={`mb-3 ${
            userData.cprProcessing ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          <FormTabs
            label={translate("Gender", "الجنس")}
            tabs={genderTabs}
            onTabClick={(type) =>
              !userData.cprProcessing && handleInputChange("gender", type)
            }
            activeTab={userData.gender}
            error={getFieldError("gender")}
          />
        </div>

        {!(hasGuestBasicInfo && policyFor === "myself") && (
          <>
        {/* Date of Birth */}
        <div className="date_of_birth mb-3">
          <label className="block mb-[5px] text-[#48535B] not-italic font-normal leading-6 text-base max-350:text-[13px]">
            {translate("Date of Birth", "تاريخ الميلاد")}
          </label>

          {/* Mobile: native date picker (visible <=767px) */}
          <div className="hidden max767:block">
            <SingleDatePicker
              value={userData.dateOfBirth?.fullDate || ""}
              onChange={handleMobileDatePickerSelect}
              placeholder={translate("Select Date", "اختر التاريخ")}
              disabled={userData.cprProcessing}
              error={getFieldError("dateOfBirth.day")}
              useNativeMobile={true}
            />
          </div>
          {/* Desktop: select dropdowns (visible >767px) */}
          <div className="block max767:hidden">
            <div className="flex gap-6 self-stretch">
              <SelectInput
                name="day"
                options_data={daysOptions}
                formData={{ day: userData.dateOfBirth.day || "" }}
                default_placeholder={translate("Day", "يوم")}
                error=""
                handleChange={(e) => {
                  const day = e.target.value;
                  const month = userData.dateOfBirth.month;
                  const year = userData.dateOfBirth.year;
                  const fullDate =
                    day && month && year
                      ? `${year}-${month.padStart(2, "0")}-${day.padStart(
                          2,
                          "0"
                        )}`
                      : "";

                  // Update state
                  const updatedDOB = {
                    day,
                    month,
                    year,
                    fullDate,
                  };
                  handleInputChange("dateOfBirth", updatedDOB);

                  // Validate date of birth in real-time when complete date is provided
                  if (day && month && year) {
                    const updatedUserData = {
                      ...userData,
                      dateOfBirth: updatedDOB,
                    };
                    const validationErrors = validateFields(updatedUserData);
                    setFieldErrors((prevErrors) => ({
                      ...prevErrors,
                      "dateOfBirth.day": validationErrors["dateOfBirth.day"] || "",
                    }));
                  }
                }}
                disabled={userData.cprProcessing}
                maxMenuHeight={185}
              />

              <SelectInput
                name="month"
                options_data={monthsOptions}
                formData={{ month: userData.dateOfBirth.month || "" }}
                default_placeholder={translate("Month", "شهر")}
                error=""
                handleChange={(e) => {
                  const month = e.target.value;
                  const day = userData.dateOfBirth.day;
                  const year = userData.dateOfBirth.year;
                  const fullDate =
                    day && month && year
                      ? `${year}-${month.padStart(2, "0")}-${day.padStart(
                          2,
                          "0"
                        )}`
                      : "";

                  // Update state
                  const updatedDOB = {
                    day,
                    month,
                    year,
                    fullDate,
                  };
                  handleInputChange("dateOfBirth", updatedDOB);

                  // Validate date of birth in real-time when complete date is provided
                  if (day && month && year) {
                    const updatedUserData = {
                      ...userData,
                      dateOfBirth: updatedDOB,
                    };
                    const validationErrors = validateFields(updatedUserData);
                    setFieldErrors((prevErrors) => ({
                      ...prevErrors,
                      "dateOfBirth.day": validationErrors["dateOfBirth.day"] || "",
                    }));
                  }
                }}
                disabled={userData.cprProcessing}
                maxMenuHeight={185}
              />

              <SelectInput
                name="year"
                options_data={yearsOptions}
                formData={{ year: userData.dateOfBirth.year || "" }}
                default_placeholder={translate("Year", "سنة")}
                error=""
                handleChange={(e) => {
                  const year = e.target.value;
                  const day = userData.dateOfBirth.day;
                  const month = userData.dateOfBirth.month;
                  const fullDate =
                    day && month && year
                      ? `${year}-${month.padStart(2, "0")}-${day.padStart(
                          2,
                          "0"
                        )}`
                      : "";

                  // Update state
                  const updatedDOB = {
                    day,
                    month,
                    year,
                    fullDate,
                  };
                  handleInputChange("dateOfBirth", updatedDOB);

                  // Validate date of birth in real-time when complete date is provided
                  if (day && month && year) {
                    const updatedUserData = {
                      ...userData,
                      dateOfBirth: updatedDOB,
                    };
                    const validationErrors = validateFields(updatedUserData);
                    setFieldErrors((prevErrors) => ({
                      ...prevErrors,
                      "dateOfBirth.day": validationErrors["dateOfBirth.day"] || "",
                    }));
                  }
                }}
                disabled={userData.cprProcessing}
                maxMenuHeight={185}
              />
            </div>
            {getFieldError("dateOfBirth.day") && (
              <p className="field-err text-red-500 text-sm mt-1">
                {getFieldError("dateOfBirth.day")}
              </p>
            )}
          </div>
        </div>
          </>
        )}
      </div>
    );
  };

  // Get total premium for footer - from API quote data or fallback to static
  return (
    <div className="w-full">
      <SmartDeviceJourneyLayout is_hide_back_btn={false}>
        <BottomStickyBtnLayout
          btn_onClick={handleContinue}
          btn_disabled={
            quoteLoading ||
            isDebouncing ||
            (hasCalledQuoteApi && !smartDeviceQuote) ||
            (policyFor === "someone_else" && !smartDeviceQuote)
          }
          btn_label={
            hasCalledQuoteApi && !smartDeviceQuote && !quoteLoading
              ? translate(
                  "Please fix the errors above",
                  "يرجى إصلاح الأخطاء أعلاه"
                )
              : translate("Continue", "التالي")
          }
          addonComponent={
            <div className="mr-10 flex max767:w-full max767:flex max767:mb-2 max767:justify-between ipad:mt-0 rtl:ml-10 rtl:mr-0">
              {!quoteLoading && !isDebouncing && smartDeviceQuote && (
                <button
                  onClick={() => setShowSummaryModal(true)}
                  className="text-[#00384D] text-center text-base not-italic font-semibold underline mr-10 rtl:ml-10 rtl:mr-0"
                >
                  {translate("Show Summary", "عرض التسعيرة")}
                </button>
              )}
              <strong className="text-[#1A2024] text-xl font-extrabold flex items-center gap-2">
                {quoteLoading || isDebouncing ? (
                  <span className="shimmer-skeleton inline-block h-6 w-24 rounded-md relative overflow-hidden bg-[#E5E9EB] before:absolute before:inset-0 before:-translate-x-full before:animate-[policyShimmer_1.4s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/70 before:to-transparent" />
                ) : smartDeviceQuote?.totalPremium ? (
                  formatFooterCurrency(smartDeviceQuote.totalPremium)
                ) : null}
              </strong>
            </div>
          }
        >
          <div className="flex flex-col items-center p-4 max767:p-0 mx-auto pt-0 py-4 pb-28">
            <div className="w-full max-w-[500px]">
              <div className="contactdetailstitle max767:ml-[0px] max767:mt-0 max767:w-full max767:pb-0 max767:p-[22px] max767:pt-0">
                <h1 className="text-[#1A2024] text-center text-2xl not-italic font-semibold leading-8 tracking-[-0.24px] max767:text-left  max767:rtl:text-right mb-2">
                  {translate("Your Information", "بياناتك")}
                </h1>
                <p className="text-[#1A2024] text-center text-base not-italic font-normal leading-6 tracking-[-0.16px] mb-6 max767:text-left  max767:rtl:text-right">
                  {translate(
                    "Fill in your information, and we'll take care of the rest.",
                    "يرجى إدخال المعلومات المطلوبة أدناه."
                  )}
                </p>
              </div>

              {renderLoggedInSection()}

              {/* User Form */}
              {(!isGuest && policyFor === "someone_else") || isGuest
                ? renderUserForm()
                : null}
            </div>
          </div>
        </BottomStickyBtnLayout>
      </SmartDeviceJourneyLayout>

      {/* Smart Device Summary Modal */}
      <SmartDeviceSummaryModal
        show={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
      />
    </div>
  );
};

export default ContactDetails;
